import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import {
  EMPTY_LICENSE_FILTERS,
  IAgentBuild,
  ILicense,
  ILicenseFilters,
  IRelease,
  LICENSE_STATUSES,
  LICENSE_TYPES,
} from 'interfaces/license.interface';
import {
  getLicenses,
  revokeLicense,
  deleteLicense,
  downloadLicense,
  downloadAgent,
  getCurrentRelease,
  downloadReleaseZip,
  downloadReleaseExtension,
  downloadReleaseNotes,
} from 'requests/license.request';
import { useModal } from 'contexts/modal.context';
import { LICENSE_TABLE_COLUMNS, PAGE_SIZE_OPTIONS } from 'constants/license.constant';

import Table from 'components/table/table.component';
import Pagination from 'components/pagination/pagination.component';
import Button from 'components/button/button.component';
import Icon from 'components/icon/icon.component';
import CreateLicenseModal from './components/create-license.modal';
import ViewLicenseModal from './components/view-license.modal';
import GenerateAgentModal from './components/generate-agent.modal';
import styles from './licenses.module.scss';

/** Typing in the search box must not fire a request per keystroke. */
const SEARCH_DEBOUNCE_MS = 350;

const LicensesPage = () => {
  const { t } = useTranslation();
  const { open, close } = useModal();

  const [licenses, setLicenses] = useState<ILicense[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);

  const [release, setRelease] = useState<IRelease>({});
  const [agentBuilds, setAgentBuilds] = useState<Record<string, IAgentBuild>>({});

  // Percent complete per in-flight download, so a click is visibly doing something. These
  // files are big enough that a button which simply does nothing for half a minute reads
  // as a click that did not register.
  // undefined = not downloading; null = downloading, length unknown; number = percent.
  // The progress callback legitimately reports null, so "idle" needs its own value.
  const [zipProgress, setZipProgress] = useState<number | null | undefined>(undefined);
  const [extProgress, setExtProgress] = useState<number | null | undefined>(undefined);
  // The notes PDF is small enough that a busy flag is all the feedback it needs.
  const [notesLoading, setNotesLoading] = useState(false);
  const [agentProgress, setAgentProgress] = useState<Record<number, number | null>>({});

  // `filters` drives the inputs; `appliedFilters` is what the server has been asked for.
  // Keeping them apart is what lets the text box stay responsive while requests lag behind.
  const [filters, setFilters] = useState<ILicenseFilters>(EMPTY_LICENSE_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<ILicenseFilters>(EMPTY_LICENSE_FILTERS);

  const hasFilters = useMemo(
    () => Object.values(filters).some(Boolean),
    [filters],
  );

  // Debounce only the free-text field. The two selects apply immediately, because a click
  // is already a deliberate action and waiting on it just feels broken.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => (prev.search === filters.search ? prev : { ...prev, search: filters.search }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    setAppliedFilters((prev) =>
      prev.type === filters.type && prev.status === filters.status
        ? prev
        : { ...prev, type: filters.type, status: filters.status },
    );
  }, [filters.type, filters.status]);

  // Page 3 of an unfiltered list is rarely page 3 of a filtered one, so any change to the
  // filters sends the user back to the first page. Skipped on the very first render.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setPage(0);
  }, [appliedFilters, pageSize]);

  // `silent` keeps the background poll from flashing the loading row every few seconds.
  const fetchLicenses = useCallback(
    (silent = false) => {
      if (!silent) setLoading(true);
      getLicenses(page, pageSize, appliedFilters)
        .then((res) => {
          setLicenses(res.content);
          setTotalElements(res.totalElements);
          setAgentBuilds((res.agentBuilds ?? {}) as Record<string, IAgentBuild>);
        })
        .catch(() => {
          if (!silent) toast.error(t('licenses.loadError'));
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [page, pageSize, appliedFilters, t],
  );

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  useEffect(() => {
    getCurrentRelease()
      .then(setRelease)
      .catch(() => setRelease({}));
  }, []);

  // Stamping happens on another machine, so the only way to learn a build finished is to
  // ask. Polling runs only while something is actually in flight.
  const hasActiveBuild = useMemo(
    () =>
      Object.values(agentBuilds).some(
        (b) => b?.status === 'PENDING' || b?.status === 'BUILDING',
      ),
    [agentBuilds],
  );

  useEffect(() => {
    if (!hasActiveBuild) return;
    const timer = setInterval(() => fetchLicenses(true), 5000);
    return () => clearInterval(timer);
  }, [hasActiveBuild, fetchLicenses]);

  const handleFilterChange = (key: keyof ILicenseFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleResetFilters = () => setFilters(EMPTY_LICENSE_FILTERS);

  const handleCreate = () => {
    open(
      <CreateLicenseModal
        onSuccess={() => {
          close();
          fetchLicenses();
        }}
      />,
      { title: t('licenses.create'), width: 600 },
    );
  };

  const handleView = (license: ILicense) => {
    open(<ViewLicenseModal license={license} />, {
      title: t('licenses.viewTitle'),
      width: 700,
    });
  };

  const saveBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Always via the dialog, whether or not the licence already has a URL: the address is
  // baked into the MSI and cannot be corrected afterwards, so it gets one confirmation.
  const handleRequestAgent = (license: ILicense) => {
    open(
      <GenerateAgentModal
        license={license}
        releaseVersion={release.version}
        onSuccess={(build) => {
          close();
          // Reflect it at once so the row switches to "queued" without waiting for a poll.
          setAgentBuilds((prev) => ({ ...prev, [String(license.id)]: build }));
          fetchLicenses(true);
        }}
      />,
      { title: t('licenses.generateAgentTitle'), width: 520 },
    );
  };

  const handleDownloadAgent = async (license: ILicense) => {
    if (license.id in agentProgress) return; // already downloading
    setAgentProgress((p) => ({ ...p, [license.id]: 0 }));
    try {
      const blob = await downloadAgent(license.id, (percent) =>
        setAgentProgress((p) => ({ ...p, [license.id]: percent })),
      );
      const build = agentBuilds[String(license.id)];
      saveBlob(blob, `CommonArk-${license.clientId}-${build?.releaseVersion ?? ''}.msi`);
    } catch {
      toast.error(t('licenses.downloadError'));
    } finally {
      setAgentProgress((p) => {
        const next = { ...p };
        delete next[license.id];
        return next;
      });
    }
  };

  const handleDownloadZip = async () => {
    if (zipProgress !== undefined) return; // already downloading
    setZipProgress(0);
    try {
      const blob = await downloadReleaseZip(setZipProgress);
      saveBlob(blob, `commonark-release-${release.version ?? 'current'}.zip`);
    } catch {
      toast.error(t('licenses.zipDownloadError'));
    } finally {
      setZipProgress(undefined);
    }
  };

  const handleDownloadExtension = async () => {
    if (extProgress !== undefined) return; // already downloading
    setExtProgress(0);
    try {
      const blob = await downloadReleaseExtension(setExtProgress);
      saveBlob(blob, `commonark-extension-${release.version ?? 'current'}.zip`);
    } catch {
      toast.error(t('licenses.extensionDownloadError'));
    } finally {
      setExtProgress(undefined);
    }
  };

  const handleDownloadReleaseNotes = async () => {
    if (notesLoading) return;
    setNotesLoading(true);
    try {
      const blob = await downloadReleaseNotes();
      saveBlob(blob, `CommonArk-release-notes-${release.version ?? 'current'}.pdf`);
    } catch {
      toast.error(t('licenses.releaseNotesDownloadError'));
    } finally {
      setNotesLoading(false);
    }
  };

  const handleDownload = async (license: ILicense) => {
    try {
      const blob = await downloadLicense(license.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `license_${license.clientId}.lic`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('licenses.downloadError'));
    }
  };

  const handleRevoke = async (license: ILicense) => {
    try {
      await revokeLicense(license.id);
      toast.success(t('licenses.revoked'));
      fetchLicenses();
    } catch {
      toast.error(t('licenses.revokeError'));
    }
  };

  const handleDelete = async (license: ILicense) => {
    try {
      await deleteLicense(license.id);
      toast.success(t('licenses.deleted'));
      fetchLicenses();
    } catch {
      toast.error(t('licenses.deleteError'));
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const handleGetTableValue = (item: ILicense, key: string) => {
    switch (key) {
      case 'status':
        return (
          <span
            className={
              item.status === 'ACTIVE' ? styles.licenses__statusActive : styles.licenses__statusRevoked
            }
          >
            {t(`licenses.statuses.${item.status}`)}
          </span>
        );
      case 'type':
        return <span className={styles.licenses__type}>{item.type}</span>;
      case 'modules': {
        // A set, not a sentence. Rendering the raw comma-joined string is what overflowed
        // the column and painted over the neighbouring one; two chips plus a counter fit
        // the width, and the full list stays reachable on hover and in the view modal.
        const modules = (item.modules || '')
          .split(',')
          .map((m) => m.trim())
          .filter(Boolean);

        if (!modules.length) return <span className={styles.licenses__muted}>—</span>;

        const shown = modules.slice(0, 3);
        const hidden = modules.length - shown.length;

        return (
          <span className={styles.licenses__chips} title={modules.join(', ')}>
            {shown.map((m) => (
              <span key={m} className={styles.licenses__chip}>
                {m}
              </span>
            ))}
            {hidden > 0 && <span className={styles.licenses__chipMore}>+{hidden}</span>}
          </span>
        );
      }
      case 'startDate':
        return formatDate(item.startDate);
      case 'endDate':
        return formatDate(item.endDate);
      case 'createdDate':
        // The full timestamp is back: it was dropped to date-only only because the sidebar
        // left no room for it, and that constraint is gone.
        return formatDateTime(item.createdDate);
      case 'agent': {
        const build = agentBuilds[String(item.id)];

        // A missing server URL is no longer a dead end - the dialog collects it and stores
        // it on the licence, so every row offers the same action.
        if (!build) {
          return (
            <button className={styles.licenses__agentBtn} onClick={() => handleRequestAgent(item)}>
              <Icon name="Package" size={14} />
              {t('licenses.generateAgent')}
            </button>
          );
        }

        if (build.status === 'PENDING' || build.status === 'BUILDING') {
          return (
            <span className={styles.licenses__agentPending}>
              <span className={styles.licenses__agentSpinner} aria-hidden="true" />
              {t(build.status === 'PENDING' ? 'licenses.agentQueued' : 'licenses.agentBuilding')}
            </span>
          );
        }

        if (build.status === 'FAILED') {
          return (
            <button
              className={styles.licenses__agentFailed}
              title={build.error || (t('licenses.agentFailed') as string)}
              onClick={() => handleRequestAgent(item)}
            >
              <Icon name="CircleAlert" size={14} />
              {t('licenses.agentFailed')}
            </button>
          );
        }

        // READY. Rebuild is always offered, not only when a newer release exists: the
        // server address is otherwise set once and unreachable, and a customer moving
        // servers is at least as common as a version going stale. Staleness still changes
        // how the button reads - amber with the version gap when there is something to
        // catch up on, quiet otherwise - so "you should" and "you may" stay distinct.
        const stale = !!release.version && build.releaseVersion !== release.version;
        return (
          <span className={styles.licenses__agentReady}>
            <button
              className={styles.licenses__agentBtn}
              title={t('licenses.downloadAgent') as string}
              disabled={item.id in agentProgress}
              onClick={() => handleDownloadAgent(item)}
            >
              {item.id in agentProgress ? (
                <>
                  <span className={styles.licenses__btnSpinner} aria-hidden="true" />
                  {agentProgress[item.id] ? `${agentProgress[item.id]}%` : t('common.loading')}
                </>
              ) : (
                <>
                  <Icon name="Download" size={14} />
                  {build.releaseVersion}
                </>
              )}
            </button>
            <button
              className={
                stale ? styles.licenses__agentRebuild : styles.licenses__agentRebuildQuiet
              }
              title={
                (stale
                  ? t('licenses.agentStale', {
                      built: build.releaseVersion,
                      current: release.version,
                    })
                  : t('licenses.regenerateHint', { url: item.backendUrl ?? '' })) as string
              }
              onClick={() => handleRequestAgent(item)}
            >
              <Icon name="RefreshCw" size={13} />
            </button>
          </span>
        );
      }
      case 'actions':
        return (
          <div className={styles.licenses__actions}>
            <button
              className="commonArk__iconBtn"
              onClick={() => handleView(item)}
              data-tooltip={t('licenses.view')}
            >
              <Icon name="Eye" size={18} />
            </button>
            <button
              className="commonArk__iconBtn"
              onClick={() => handleDownload(item)}
              data-tooltip={t('licenses.download')}
            >
              <Icon name="Download" size={18} />
            </button>
            {item.status === 'ACTIVE' && (
              <button
                className="commonArk__iconBtn"
                onClick={() => handleRevoke(item)}
                data-tooltip={t('licenses.revoke')}
              >
                <Icon name="Ban" size={18} />
              </button>
            )}
            <button
              className="commonArk__iconBtn"
              onClick={() => handleDelete(item)}
              data-tooltip={t('licenses.delete')}
            >
              <Icon name="Trash2" size={18} />
            </button>
          </div>
        );
      case 'clientId':
        return <span title={item.clientId}>{item.clientId}</span>;
      default:
        return item[key as keyof ILicense];
    }
  };

  return (
    <div>
      <div className="commonArk__header">
        <h1 className="commonArk__title">{t('licenses.pageTitle')}</h1>
        <Button primary onClick={handleCreate}>
          {t('licenses.create')}
        </Button>

        {/* The server zip is the same for every customer, so it belongs to the page rather
            than to any row. Disabled with the reason when no release has been published. */}
        <button
          className={styles.licenses__zipBtn}
          onClick={handleDownloadZip}
          disabled={!release.hasZip || zipProgress !== undefined}
          title={
            release.hasZip
              ? `commonark-release-${release.version}.zip`
              : (t('licenses.noRelease') as string)
          }
        >
          {zipProgress !== undefined ? (
            <>
              <span className={styles.licenses__btnSpinner} aria-hidden="true" />
              {zipProgress ? `${zipProgress}%` : t('common.loading')}
            </>
          ) : (
            <>
              <Icon name="FileArchive" size={16} />
              {t('licenses.downloadZip')}
              {release.version && (
                <span className={styles.licenses__zipVersion}>{release.version}</span>
              )}
            </>
          )}
        </button>

        {/* The unpacked browser-extension zip, for the manual load-unpacked dev setup. Same
            for every customer, so it lives on the page like the server zip. */}
        <button
          className={styles.licenses__zipBtn}
          onClick={handleDownloadExtension}
          disabled={!release.hasExtension || extProgress !== undefined}
          title={
            release.hasExtension
              ? `commonark-extension-${release.version}.zip`
              : (t('licenses.noExtension') as string)
          }
        >
          {extProgress !== undefined ? (
            <>
              <span className={styles.licenses__btnSpinner} aria-hidden="true" />
              {extProgress ? `${extProgress}%` : t('common.loading')}
            </>
          ) : (
            <>
              <Icon name="Puzzle" size={16} />
              {t('licenses.downloadExtension')}
              {release.version && (
                <span className={styles.licenses__zipVersion}>{release.version}</span>
              )}
            </>
          )}
        </button>

        {/* What changed, version by version, up to the current release - the same PDF the
            server zip carries as RELEASE-NOTES.pdf, for passing on to a customer. */}
        <button
          className={styles.licenses__zipBtn}
          onClick={handleDownloadReleaseNotes}
          disabled={!release.hasReleaseNotes || notesLoading}
          title={
            release.hasReleaseNotes
              ? `CommonArk-release-notes-${release.version}.pdf`
              : (t('licenses.noReleaseNotes') as string)
          }
        >
          {notesLoading ? (
            <>
              <span className={styles.licenses__btnSpinner} aria-hidden="true" />
              {t('common.loading')}
            </>
          ) : (
            <>
              <Icon name="FileText" size={16} />
              {t('licenses.downloadReleaseNotes')}
              {release.version && (
                <span className={styles.licenses__zipVersion}>{release.version}</span>
              )}
            </>
          )}
        </button>
      </div>

      <div className="commonArk__wrapper">
        <div className={styles.licenses__toolbar}>
          <div className={styles.licenses__search}>
            <Icon name="Search" size={16} />
            <input
              type="text"
              value={filters.search}
              placeholder={t('licenses.searchPlaceholder') as string}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              aria-label={t('licenses.searchPlaceholder') as string}
            />
            {filters.search && (
              <button
                type="button"
                className={styles.licenses__searchClear}
                onClick={() => handleFilterChange('search', '')}
                aria-label={t('common.clear') as string}
              >
                <Icon name="X" size={14} />
              </button>
            )}
          </div>

          <select
            className={styles.licenses__filterSelect}
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            aria-label={t('licenses.type') as string}
          >
            <option value="">{t('licenses.allTypes')}</option>
            {LICENSE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          <select
            className={styles.licenses__filterSelect}
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            aria-label={t('licenses.status') as string}
          >
            <option value="">{t('licenses.allStatuses')}</option>
            {LICENSE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`licenses.statuses.${status}`)}
              </option>
            ))}
          </select>

          {hasFilters && (
            <button type="button" className={styles.licenses__reset} onClick={handleResetFilters}>
              <Icon name="X" size={14} />
              {t('common.reset')}
            </button>
          )}

          <span className={styles.licenses__count}>
            {t('licenses.totalCount', { total: totalElements })}
          </span>
        </div>

        <Table
          columns={LICENSE_TABLE_COLUMNS}
          data={licenses}
          translateKey="licenses"
          loading={loading}
          emptyText={hasFilters ? (t('licenses.noResults') as string) : undefined}
          handleGetTableValue={handleGetTableValue}
        />

        <div className={styles.licenses__footer}>
          <label className={styles.licenses__pageSize}>
            {t('common.perPage')}
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              aria-label={t('common.perPage') as string}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <Pagination
            totalItems={totalElements}
            itemsPerPage={pageSize}
            currentPage={page}
            onPageChange={setPage}
          />
        </div>
      </div>
    </div>
  );
};

export default LicensesPage;
